import React from 'react';
import {
  arrayUtils,
  colors,
  Headline,
  moneyUtils,
  timeUtils,
} from '@sato/components';
import { observer } from 'mobx-react-lite';
import { twMerge } from 'tailwind-merge';

import { coldWaterColor, warmWaterColor } from '@components/Charts/chartTheme';
import { localizationStore } from '@stores/localization';

import { deserializeMeasurements } from '../WaterUsage.queries';

import { WaterListItem } from './WaterListItem';

interface WaterUsageSummaryProps {
  measurements: ReturnType<typeof deserializeMeasurements>;
  className?: string;
}

export const WaterUsageSummary = observer(
  ({ measurements: { summary, items }, className }: WaterUsageSummaryProps) => {
    const t = localizationStore.getTranslation;
    const language = localizationStore.language;

    const formatAmount = (amount: number) => {
      const m3 = (amount / 1000).toFixed(2);
      return `${Math.round(amount)} ${t('liters')} (${m3}m³)`;
    };

    const formatCost = (amountLiters: number, cost: number | undefined) => {
      if (cost === undefined) {
        return undefined;
      }

      const m3 = (amountLiters / 1000).toFixed(2);
      const formattedCost = moneyUtils.formatMoney(
        { currency: 'EUR', value: cost },
        language
      );

      return `${formattedCost} (${m3}m³)`;
    };

    const formatDate = (date: Date | undefined) =>
      date &&
      timeUtils
        .formatDate({
          date,
          dateFormat: timeUtils.DATE_FORMATS.DATE_AND_TIME_SHORT,
          locale: language,
        })
        // Replace with non-breaking spaces so that the date wraps intelligently on smaller screens
        .replaceAll(' ', '\u00A0');

    const start = formatDate(items[0]?.previousTimestamp);
    const end = formatDate(items[items.length - 1]?.timeStamp);

    const measurementCategories = [
      {
        title: t('waterUsageTotalWaterLabel'),
        amount: formatAmount(summary.total),
        color: colors['black-text'],
        cost: undefined as string | undefined,
      },
      {
        title: t('waterUsageColdWaterLabel'),
        amount: formatAmount(summary.cold),
        color: coldWaterColor,
        cost: formatCost(summary.cold, summary.costs?.cold),
      },
      {
        title: t('waterUsageWarmWaterLabel'),
        amount: formatAmount(summary.warm),
        color: warmWaterColor,
        cost: formatCost(summary.warm, summary.costs?.warm),
      },
    ];

    return (
      <div
        className={twMerge('flex flex-col gap-3 md:items-center', className)}
      >
        <Headline
          level={3}
          className="break-words !font-header !text-5 lg:text-center"
        >
          {t('waterUsageTotalUsageTitle')}{' '}
          {arrayUtils.joinArrayString([start, end], '\u00A0– ')}
        </Headline>

        <div className="flex flex-col justify-center gap-x-5 gap-y-3 md:flex-row">
          {measurementCategories.map(({ title, amount, color, cost }) => (
            <WaterListItem key={title} color={color} title={title} price={cost}>
              {amount}
            </WaterListItem>
          ))}
        </div>
      </div>
    );
  }
);
